import {
  Button,
  Input,
  Empty,
} from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import './index.css';
import { Link, useParams } from 'react-router-dom';
import { getFixtures } from '../../apis/fixture.api';
import {
  useEffect,
  useState,
  useMemo,
  useCallback,
  useRef,
  startTransition,
} from 'react';
import {
  categories,
  HOME_PATH,
  MENU_CATEGORIES,
  PAGE_TITLES,
  SITE_NAME,
  SITE_URL,
} from '../../utils/constant';
import Loader from '../../components/common/Loader';
import ConnectionBanner from '../../components/common/ConnectionBanner';
import { styles } from '../../styles';
import JoinDiscord from '../../components/social/discord';
import SportCategoriesStrip from '../../components/SportCategoriesStrip';
import BrandMark from '../../components/BrandMark';
import {
  fixtureListCacheKey,
  isFixtureListResponse,
  readSessionCache,
  resilientFetch,
} from '../../utils/resilientFetch';
import { applyPageSeo, organizationSchema } from '../../utils/seo';
import { CategoryCard } from './HomeFixtureCards';
import DeferredHomeDescription from './DeferredHomeDescription';
import CategoryDescription from './CategoryDescription';
import { getCategorySeoContent } from './categorySeoContent';

const searchInputStyle: React.CSSProperties = {
  borderRadius: '6px',
  width: '100%',
};

function applyFixturesPayload(
  payload: any,
  setFixtures: (v: any) => void,
  setNoFixtures: (v: boolean) => void,
) {
  if (!payload?.subCategories?.[0]?.games?.length) {
    setNoFixtures(true);
  } else {
    setNoFixtures(false);
  }
  setFixtures(payload);
}

function resolveCategoryLabel(categoryName?: string): string {
  if (!categoryName) return '';
  const fromMenu = MENU_CATEGORIES.find(
    (cat) => cat.value.toLowerCase() === categoryName.toLowerCase()
      || cat.label.toLowerCase() === categoryName.toLowerCase(),
  );
  if (fromMenu) return fromMenu.label;
  const seo = getCategorySeoContent(categoryName);
  return seo?.label || categoryName;
}

export default function Home() {
  const { categoryName } = useParams();
  const cacheKey = fixtureListCacheKey(categoryName);
  const cachedEntry = readSessionCache<any>(cacheKey);
  const categoryLabel = resolveCategoryLabel(categoryName);

  const [fixtures, setFixtures] = useState<any>(() => cachedEntry?.data?.data ?? []);
  const [noFixtures, setNoFixtures] = useState<boolean>(() => {
    const payload = cachedEntry?.data?.data;
    return Boolean(payload && !payload?.subCategories?.[0]?.games?.length);
  });
  const [loader, setLoader] = useState<boolean>(() => !cachedEntry?.data?.data);
  const [fetchError, setFetchError] = useState<boolean>(false);
  const fetchIdRef = useRef(0);

  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const getAllFixtures = useCallback(async (force = false) => {
    const requestId = ++fetchIdRef.current;
    const selectedCat = categoryName ?? 'Streams Today';
    const categoryObj = categories.find(
      (cat: any) => cat.label.toLowerCase() === selectedCat?.toLowerCase(),
    );
    const category = categoryObj?.value;
    const key = fixtureListCacheKey(categoryName);
    const existing = readSessionCache<any>(key);
    const hasCachedPayload = Boolean(existing?.data?.data);

    if (!hasCachedPayload) {
      setLoader(true);
    }

    const result = await resilientFetch<any>({
      key,
      fetcher: () => getFixtures({ categoryName: category }),
      isSuccess: isFixtureListResponse,
      skipIfFresh: !force,
      staggerMs: force ? 0 : undefined,
    });

    if (requestId !== fetchIdRef.current) return;

    if (result.data?.data) {
      applyFixturesPayload(result.data.data, setFixtures, setNoFixtures);
      setFetchError(result.error);
    } else if (!hasCachedPayload) {
      setNoFixtures(true);
      setFixtures([]);
      setFetchError(true);
    } else {
      setFetchError(true);
    }

    setLoader(false);
  }, [categoryName]);

  const filteredFixtures = useMemo(() => {
    if (!fixtures?.subCategories) return fixtures;

    let filteredSubCategories = fixtures.subCategories;

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filteredSubCategories = filteredSubCategories
        .map((cat: any) => {
          const filteredGames = (cat.games || []).filter((game: any) => (
            game.teamA?.toLowerCase().includes(query)
            || game.teamB?.toLowerCase().includes(query)
            || cat.subCategoryName?.toLowerCase().includes(query)
          ));
          return { ...cat, games: filteredGames };
        })
        .filter((cat: any) => cat.games && cat.games.length > 0);
    }

    return { ...fixtures, subCategories: filteredSubCategories };
  }, [fixtures, searchQuery]);

  const gamesFound = useMemo(() => (
    filteredFixtures?.subCategories?.reduce(
      (acc: number, cat: any) => acc + (cat.games?.length || 0),
      0,
    ) || 0
  ), [filteredFixtures]);

  const handleSearchChange = useCallback((value: string) => {
    setSearchInput(value);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      startTransition(() => setSearchQuery(value));
    }, 200);
  }, []);

  useEffect(() => () => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
  }, []);

  useEffect(() => {
    const seo = categoryName ? getCategorySeoContent(categoryName) : null;
    const title = categoryName
      ? (PAGE_TITLES[categoryName?.toUpperCase()]
        || PAGE_TITLES[seo?.key || '']
        || `${SITE_NAME} – Free ${categoryLabel} Streams`)
      : PAGE_TITLES.WEBSITE_TITLE;
    const description = categoryName
      ? (seo?.metaDescription
        || `Watch free ${categoryLabel} live streams on ${SITE_NAME}. Updated fixtures and HD links — no signup required.`)
      : 'Buffstreams offers free HD live sports streaming – NBA, NFL, Boxing, MMA, F1, soccer, football and more. No signup required.';
    const path = categoryName ? `/streams/${categoryName}` : '/';
    const schemaId = 'home-page-schema';

    const cleanupSeo = applyPageSeo({
      title,
      description,
      path,
      keywords: categoryName
        ? `${categoryLabel} streams, free ${categoryLabel} live stream, buffstreams, buffstream.st`
        : 'buffstreams, buffstream.st, free sports streams, NFL, NBA, UFC, boxing, F1, soccer',
      schemaId,
      schema: {
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: title,
        description,
        url: categoryName ? `${SITE_URL}/streams/${categoryName}` : `${SITE_URL}/`,
        isPartOf: {
          '@type': 'WebSite',
          name: SITE_NAME,
          url: SITE_URL,
        },
        about: categoryName
          ? { '@type': 'Thing', name: `${categoryLabel} live streams` }
          : { '@type': 'Thing', name: 'Live sports streaming' },
        publisher: organizationSchema(),
      },
    });

    const key = fixtureListCacheKey(categoryName);
    const existing = readSessionCache<any>(key);
    if (existing?.data?.data) {
      applyFixturesPayload(existing.data.data, setFixtures, setNoFixtures);
      setLoader(false);
    } else {
      setFixtures([]);
      setNoFixtures(false);
      setLoader(true);
    }
    setFetchError(false);
    setSearchInput('');
    setSearchQuery('');
    getAllFixtures();

    return cleanupSeo;
  }, [categoryName, categoryLabel, getAllFixtures]);

  return (
    <div className="bs-home">
      {categoryName ? (
        <div className="bs-category-page-title">
          <h1>
            <BrandMark size="md" />
            <span className="bs-title-rest">{`Free ${categoryLabel} Streams`}</span>
          </h1>
        </div>
      ) : (
        <div className="bs-brand-hero">
          <h1>
            <BrandMark size="lg" />
            <span className="bs-title-rest">
              Live Sports Streaming for NBA, NFL, Boxing, MMA, and F1
            </span>
          </h1>
          <p>Free HD streams for soccer, football, and every major sport — no signup required.</p>
        </div>
      )}

      <div style={{ paddingTop: 4 }}>
        <JoinDiscord />
      </div>

      <SportCategoriesStrip />

      <div className="bs-search-wrap">
        <Input
          placeholder="Search fixtures, teams, or categories..."
          prefix={<SearchOutlined style={{ color: 'var(--bs-muted)' }} />}
          value={searchInput}
          onChange={(e) => handleSearchChange(e.target.value)}
          size="large"
          style={searchInputStyle}
          allowClear
        />
        {searchQuery && (
          <div className="bs-search-count">{gamesFound} games found</div>
        )}
      </div>

      <ConnectionBanner
        visible={fetchError && !loader}
        onRetry={() => getAllFixtures(true)}
      />

      {loader ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 32 }}>
          <Loader />
        </div>
      ) : categoryName && noFixtures ? (
        <div style={{ textAlign: 'center', padding: 24 }}>
          <h2 style={{ color: 'var(--bs-text)' }}>
            No Fixtures Found For {categoryLabel.toUpperCase()}
          </h2>
          <Link to={HOME_PATH}>
            <Button style={styles.homeButton}>Home</Button>
          </Link>
        </div>
      ) : filteredFixtures?.subCategories?.length > 0 ? (
        filteredFixtures.subCategories.map((category: any) => (
          <CategoryCard
            key={category.subCategoryName || category.subCategoryImage}
            category={category}
            categoryName={categoryName}
          />
        ))
      ) : (
        <Empty
          className="bs-empty-state"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            <span style={{ color: 'var(--bs-muted)' }}>
              {searchQuery
                ? 'No fixtures found matching your search'
                : 'No fixtures available'}
            </span>
          }
        >
          {searchQuery && (
            <Button
              type="primary"
              onClick={() => {
                setSearchInput('');
                setSearchQuery('');
              }}
            >
              Clear Search
            </Button>
          )}
        </Empty>
      )}

      {!loader && !categoryName && (
        <>
          <DeferredHomeDescription />
          <section className="bs-blog-section">
            <h2>Categories</h2>
            <p>
              Browse every sport on Buffstreams — soccer, NFL, NBA, UFC, boxing, F1, and more.
            </p>
            <Link to="/categories" className="bs-blog-cta">
              View all categories
            </Link>
          </section>
        </>
      )}
      {!loader && categoryName && (
        <CategoryDescription categoryName={categoryName} />
      )}
    </div>
  );
}
